/**
 * Debug / QA flags for hotel search pipeline visibility.
 *
 * Enable without rebuild:
 *   localStorage.setItem('SHOW_ALL_HOTEL_RESULTS_DEBUG', 'true')
 *
 * Or in .env (Vite — must be prefixed with VITE_):
 *   VITE_SHOW_ALL_HOTEL_RESULTS_DEBUG=true
 */

function getImportMetaEnv() {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env) {
      return import.meta.env;
    }
  } catch (_) {
    /* ignore */
  }
  return {};
}

/**
 * Audit log [HOTEL_CARD_PRICE_DEBUG] for hotel list card vs raw search prices.
 *   localStorage.setItem('HOTEL_CARD_PRICE_DEBUG', '1')
 * Verbose: one line per hotel
 *   localStorage.setItem('HOTEL_CARD_PRICE_DEBUG_VERBOSE', '1')
 */
export function isHotelCardPriceDebugEnabled() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("HOTEL_CARD_PRICE_DEBUG") === "1"
    );
  } catch (_) {
    return false;
  }
}

export function isHotelCardPriceDebugVerbose() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("HOTEL_CARD_PRICE_DEBUG_VERBOSE") === "1"
    );
  } catch (_) {
    return false;
  }
}

/**
 * מסלול מחיר מלון מקצה לקצה (כרטיס → פרופיל → סיכום).
 *   localStorage.setItem('HOTEL_PRICE_TRACE', '1')
 * ראה: utils/hotelPriceTrace.js
 */

/**
 * Unified hotel selling-price resolver traces.
 *   localStorage.setItem('HOTEL_PRICING_DEBUG', '1')
 * Or: VITE_HOTEL_PRICING_DEBUG=1
 */
export function isHotelPricingDebug() {
  try {
    if (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("HOTEL_PRICING_DEBUG") === "1"
    ) {
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  const env = getImportMetaEnv();
  if (
    env.VITE_HOTEL_PRICING_DEBUG === "1" ||
    env.VITE_HOTEL_PRICING_DEBUG === true
  ) {
    return true;
  }
  return false;
}

/**
 * פרופיל מלון — אודיט מחירים אחיד [PRICE_AUDIT_PROFILE]
 *   localStorage.setItem('HR_PROFILE_PRICE_AUDIT', '1')
 */
export function isHotelProfilePriceAuditEnabled() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("HR_PROFILE_PRICE_AUDIT") === "1"
    );
  } catch (_) {
    return false;
  }
}

/**
 * HOTELS tab: use hotelSearchEngine + useHotelResultsFilters SSOT path in searchresults.jsx.
 * Default ON. Disable without rebuild:
 *   localStorage.setItem('USE_HOTEL_ENGINE_SSOT', 'false')
 * Or: VITE_USE_HOTEL_ENGINE_SSOT=false
 */
export function isHotelEngineSsotEnabled() {
  try {
    if (typeof window !== "undefined") {
      const ls = window.localStorage?.getItem("USE_HOTEL_ENGINE_SSOT");
      if (ls === "false") return false;
      if (ls === "true") return true;
    }
  } catch (_) {
    /* ignore */
  }
  const env = getImportMetaEnv();
  if (
    env.VITE_USE_HOTEL_ENGINE_SSOT === "false" ||
    env.VITE_USE_HOTEL_ENGINE_SSOT === false
  ) {
    return false;
  }
  if (
    env.VITE_USE_HOTEL_ENGINE_SSOT === "true" ||
    env.VITE_USE_HOTEL_ENGINE_SSOT === true
  ) {
    return true;
  }
  return true;
}

export function isShowAllHotelResultsDebug() {
  try {
    if (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("SHOW_ALL_HOTEL_RESULTS_DEBUG") === "true"
    ) {
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  const env = getImportMetaEnv();
  if (
    env.VITE_SHOW_ALL_HOTEL_RESULTS_DEBUG === "true" ||
    env.REACT_APP_SHOW_ALL_HOTEL_RESULTS_DEBUG === "true"
  ) {
    return true;
  }
  try {
    if (
      typeof process !== "undefined" &&
      process.env &&
      process.env.REACT_APP_SHOW_ALL_HOTEL_RESULTS_DEBUG === "true"
    ) {
      return true;
    }
  } catch (_) {
    /* ignore — process may be undefined in browser */
  }
  return false;
}

/** Verbose pipeline logs: development, DEBUG_SEARCH localStorage, or SHOW_ALL flag */
/**
 * Multi-room split search (backend /searchHotelsComposite). Off by default.
 *
 *   VITE_ENABLE_MULTI_ROOM_SPLIT_SEARCH=1
 *   or localStorage MULTI_ROOM_SPLIT_SEARCH = '1'
 */
/**
 * Multi-room booking orchestration (POST /bookCompositeHotel). Off by default.
 *   VITE_ENABLE_MULTI_ROOM_BOOKING_FLOW=1
 *   or localStorage MULTI_ROOM_BOOKING_FLOW = '1'
 */
export function isMultiRoomBookingFlowEnabled() {
  try {
    if (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("MULTI_ROOM_BOOKING_FLOW") === "1"
    ) {
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  const env = getImportMetaEnv();
  if (
    env.VITE_ENABLE_MULTI_ROOM_BOOKING_FLOW === "1" ||
    env.VITE_ENABLE_MULTI_ROOM_BOOKING_FLOW === true
  ) {
    return true;
  }
  return false;
}

export function isMultiRoomSplitSearchEnabled() {
  try {
    if (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("MULTI_ROOM_SPLIT_SEARCH") === "1"
    ) {
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  const env = getImportMetaEnv();
  if (
    env.VITE_ENABLE_MULTI_ROOM_SPLIT_SEARCH === "1" ||
    env.VITE_ENABLE_MULTI_ROOM_SPLIT_SEARCH === true
  ) {
    return true;
  }
  return false;
}

/**
 * Sequential multi-room hotel wizard (profile picks one package per room_request).
 *   VITE_ENABLE_MULTI_ROOM_HOTEL_WIZARD=1
 *   or localStorage MULTI_ROOM_HOTEL_WIZARD = '1'
 */
export function isMultiRoomHotelWizardEnabled() {
  try {
    if (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("MULTI_ROOM_HOTEL_WIZARD") === "1"
    ) {
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  const env = getImportMetaEnv();
  if (
    env.VITE_ENABLE_MULTI_ROOM_HOTEL_WIZARD === "1" ||
    env.VITE_ENABLE_MULTI_ROOM_HOTEL_WIZARD === true
  ) {
    return true;
  }
  return false;
}

/**
 * When true, hotel search must use standard searchHotels only (no /searchHotelsComposite).
 * Multi-room composition is chosen sequentially in HotelProfile.
 * @param {unknown} rooms — e.g. searchPhrase.rooms
 */
export function shouldSkipCompositeHotelSearchForWizard(rooms) {
  return (
    isMultiRoomHotelWizardEnabled() &&
    Array.isArray(rooms) &&
    rooms.length >= 2
  );
}

/**
 * Verbose wizard state logs on HotelProfile (multi-room wizard). Off by default.
 *   localStorage.setItem('DEBUG_HOTEL_PROFILE_WIZARD', '1')
 */
export function shouldLogHotelProfileWizardState() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("DEBUG_HOTEL_PROFILE_WIZARD") === "1"
    );
  } catch (_) {
    return false;
  }
}

/**
 * Multi-room wizard: occupancy filter diagnostics ([MULTI_ROOM_DEBUG] in console).
 *   localStorage.setItem('DEBUG_HOTEL_PROFILE_MULTI_ROOM', '1')
 */
export function isHotelProfileMultiRoomDeepDebugEnabled() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("DEBUG_HOTEL_PROFILE_MULTI_ROOM") === "1"
    );
  } catch (_) {
    return false;
  }
}

/**
 * Order summary: multi-room leg source / fallbacks.
 *   localStorage.setItem('HOTEL_ORDER_SUMMARY_MULTI_DEBUG', '1')
 */
export function isHotelOrderSummaryMultiRoomDebug() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("HOTEL_ORDER_SUMMARY_MULTI_DEBUG") === "1"
    );
  } catch (_) {
    return false;
  }
}

/**
 * HotelProfile load path: phrase source, rooms, hotelInformation payload.
 *   localStorage.setItem('DEBUG_HOTEL_PROFILE_LOAD', '1')
 */
export function shouldLogHotelProfileLoad() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("DEBUG_HOTEL_PROFILE_LOAD") === "1"
    );
  } catch (_) {
    return false;
  }
}

/** True when POST /bookCompositeHotel orchestration should be available (legacy flag or wizard). */
export function isMultiRoomCompositeOrchestrationEnabled() {
  return isMultiRoomBookingFlowEnabled() || isMultiRoomHotelWizardEnabled();
}

/**
 * התאמת שורות lds_suppliers ב-calculateLDSDealFare (חבילות נופש LDS).
 * localStorage.setItem('LDS_SUPPLIER_MATCH_DEBUG', '1')
 */
export function isLdsSupplierMatchDebugEnabled() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("LDS_SUPPLIER_MATCH_DEBUG") === "1"
    );
  } catch (_) {
    return false;
  }
}

/**
 * נתיב commission_type / commissionPercent ב-calculateLDSDealFare (חבילות LDS).
 * localStorage.setItem('LDS_COMMISSION_TYPE_DEBUG', '1')
 */
export function isLdsCommissionTypeDebugEnabled() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("LDS_COMMISSION_TYPE_DEBUG") === "1"
    );
  } catch (_) {
    return false;
  }
}

/**
 * דירוג חוקי lds_suppliers + נתיב fallback (חבילות LDS).
 * localStorage.setItem('LDS_RULE_MATCH_DEBUG', '1')
 */
export function isLdsRuleMatchDebugEnabled() {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("LDS_RULE_MATCH_DEBUG") === "1"
    );
  } catch (_) {
    return false;
  }
}

export function shouldLogHotelSearchPipeline() {
  const env = getImportMetaEnv();
  if (env.DEV === true || env.MODE === "development") return true;
  try {
    if (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("DEBUG_SEARCH") === "true"
    ) {
      return true;
    }
  } catch (_) {
    /* ignore */
  }
  return isShowAllHotelResultsDebug();
}

/**
 * @param {object|null} hotel
 * @param {string} stage
 * @param {string} reason
 * @param {string} file
 * @param {string} fn
 */
export function logDroppedHotel(hotel, stage, reason, file, fn) {
  const hotelId = hotel?.ID ?? hotel?.Id ?? hotel?.id ?? "";
  const hotelName = String(hotel?.DisplayName || hotel?.Name || "")
    .replace(/\s+/g, " ")
    .trim();
  console.warn(
    `[DROPPED_HOTEL] hotelId=${hotelId} hotelName=${hotelName} stage=${stage} reason=${reason} file=${file} function=${fn}`
  );
}
